import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import clearMessages from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ClearMessages";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import setInitialValues from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetInitialValues";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetIsPreloadingData";
import updateBillTo from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateBillTo";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import updateShipTo from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateShipTo";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import getProductCategories from "@waytek/Handlers/Utilities/CustomGetProductCategories";
import { trackAddShippingInfoEvent, trackBeginCheckoutEvent } from "@waytek/Handlers/Utilities/CustomTracking";
import validateEmailAndAddress from "@waytek/Store/Pages/CheckoutShipping/Handlers/ValidateEmailAndAddress";
import { CustomState } from "@waytek/Store/Reducers";
import { AvalaraAddressModel, AvalaraValidationResponse } from "@waytek/Types/AvalaraValidationModel";
import { parseBoolean } from "@waytek/Utilities/global/helpers";
import AvalaraValidationComponentModal from "@waytek/Widgets/Custom/AvalaraValidationComponentModal";
import React, { Component, createContext } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    preloadCheckoutShippingData: makeHandlerChainAwaitable(preloadCheckoutShippingData),
    setIsPreloadingData,
    updateCart: makeHandlerChainAwaitable(updateCart),
    setInitialValues,
    clearMessages,
    validateEmailAndAddress,
    updateBillTo,
    updateShipTo,
    getProductCategories,
};

const mapStateToProps = (state: CustomState) => {
    const { cartId } = state.pages.checkoutShipping;
    const billToData = state.checkoutShipping.billToData;
    const shipToData = state.checkoutShipping.shipToData;
    const cartState = getCurrentCartState(state);

    return {
        cartId,
        isPreloadingData: state.pages.checkoutShipping.isPreloadingData,
        reviewAndSubmitPageLink: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage"),
        location: getLocation(state),
        billToData,
        shipToData,
        defaultUseBilling: state.checkoutShipping.defaultUseBilling,
        cartPageLink: getPageLinkByPageType(state, "CartPage")?.url,
        isGuest: state.context.session?.isGuest || !state.context.session.isAuthenticated,
        cartState,
    };
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

type Validator = () => Promise<boolean>;

type State = {
    formSubmitAttempted: boolean;
    validators: {
        [key: string]: Validator | undefined;
    };
    isRedirecting: boolean;

    showValidationModal: boolean;
    enteredBtAddress: AvalaraAddressModel;
    enteredStAddress: AvalaraAddressModel;
    validationAddresses: AvalaraValidationResponse;
    customCategories: any;
};

export const CheckoutShippingFormContext = createContext<State>({
    formSubmitAttempted: false,
    validators: {},
    isRedirecting: false,

    showValidationModal: false,
    enteredBtAddress: {},
    enteredStAddress: {},
    validationAddresses: {},
    customCategories: {},
});

export const beginCheckoutTriggered = "BeginCheckoutTriggered_Cookie";

class CheckoutShippingPage extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            formSubmitAttempted: false,
            validators: {},
            isRedirecting: false,

            showValidationModal: false,
            enteredBtAddress: {},
            enteredStAddress: {},
            validationAddresses: {},
            customCategories: {},
        };
    }

    async componentDidMount() {
        if (!this.props.isPreloadingData) {
            const { cartId } = parseQueryString<{ cartId?: string }>(this.props.location.search);
            await this.props.preloadCheckoutShippingData({ cartId });
            this.props.setInitialValues();
        }
        this.props.setIsPreloadingData({ isPreloadingData: false });
        this.props.clearMessages();

        /* Verndale Custom Feature - START */
        if (this.props.cartState.value && this.props.cartState.value?.cartLines) {
            const customCartLines = this.props.cartState.value;

            const productIds = this.props.cartState.value.cartLines.map(cartLine => cartLine.productId) as string[];

            this.props.getProductCategories({
                ProductIds: productIds,
                onComplete: result => {
                    if (result && result.apiResult) {
                        this.setState({ customCategories: result.apiResult.productProperties });
                        const alreadyTriggerBeginCheckout = getCookie(beginCheckoutTriggered) === "true";
                        if (!alreadyTriggerBeginCheckout) {
                            trackBeginCheckoutEvent(customCartLines, result.apiResult.productProperties);
                            setCookie(beginCheckoutTriggered, "true");
                        }
                    }
                },
            });
        }
        /* Verndale Custom Feature - END */
    }

    scrollToFirstFormError = () => {
        const firstErrorElement = document.querySelector("[aria-invalid='true']");
        if (firstErrorElement) {
            const formFieldParent = firstErrorElement.closest("[class*='FormFieldStyle']");
            if (!formFieldParent) {
                return;
            }
            const { top } = formFieldParent.getBoundingClientRect();
            window.scrollTo({
                top: top + window.pageYOffset,
                left: 0,
                behavior: "smooth",
            });
        }
    };

    handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        this.setState({ formSubmitAttempted: true });

        const validators: Validator[] = [];
        Object.keys(this.state.validators).forEach(key => {
            const validator = this.state.validators[key];
            if (validator) {
                validators.push(validator);
            }
        });
        const results = await Promise.all(validators.map(v => v()));

        if (results.every(o => o)) {
            if (this.props.isGuest) {
                this.handleValidateEmailAndAddress();
            } else {
                this.redirectToReviewAndSubmit();
            }
        } else {
            this.scrollToFirstFormError();
        }
    };

    redirectToReviewAndSubmit = async () => {
        this.setState({ isRedirecting: true });
        const continueUrl = this.props.cartId
            ? `${this.props.reviewAndSubmitPageLink!.url}?cartId=${this.props.cartId}`
            : this.props.reviewAndSubmitPageLink!.url;
        // TODO ISC-12556
        // XNG-Changes: prevent redirection if cart shipping fails
        await this.props.updateCart({
            onComplete: (props: any) => {
                if (!parseBoolean(props.cart?.properties.isShippingFailed)) {
                    this.props.history.push(continueUrl);
                }
            },
        });

        /* Verndale Custom Feature - START */
        if (this.props.cartState.value && this.props.cartState.value.cartLines && this.state.customCategories) {
            trackAddShippingInfoEvent(this.props.cartState.value, this.state.customCategories);
        }

        setCookie(beginCheckoutTriggered, "false");
        /* Verndale Custom Feature - END */
    };

    handleValidateEmailAndAddress = () => {
        this.props.validateEmailAndAddress({
            billToAddress: this.props.billToData,
            shipToAddress: this.props.shipToData,
            useBillingAddress: this.props.defaultUseBilling,
            onError: error => {
                if (error.emailAlreadyExists) {
                    window.scrollTo({
                        top: window.outerHeight,
                        left: 0,
                        behavior: "smooth",
                    });
                } else if (error.avalaraValidationFailed) {
                    this.setState({
                        showValidationModal: true,
                        enteredBtAddress: {
                            line1: this.props.billToData.address1,
                            line2: this.props.billToData.address2,
                            line3: this.props.billToData.address3,
                            city: this.props.billToData.city,
                            country: this.props.billToData.country?.name,
                            postalCode: this.props.billToData.postalCode,
                            region: this.props.billToData.state?.abbreviation,
                        },
                        enteredStAddress: {
                            line1: this.props.shipToData.address1,
                            line2: this.props.shipToData.address2,
                            line3: this.props.shipToData.address3,
                            city: this.props.shipToData.city,
                            country: this.props.shipToData.country?.name,
                            postalCode: this.props.shipToData.postalCode,
                            region: this.props.shipToData.state?.abbreviation,
                        },
                        validationAddresses: {
                            btValidateAddressResponse: error.btValidateAddressResponse,
                            stValidateAddressResponse: error.stValidateAddressResponse,
                        },
                    });
                }
            },
            onSuccess: success => {
                this.redirectToReviewAndSubmit();
            },
        });
    };

    handleCloseValidationModal = () => {
        this.setState({
            showValidationModal: false,
        });
    };

    handleSubmitValidationModal = (btAddress?: AvalaraAddressModel, stAddress?: AvalaraAddressModel) => {
        const newBillTo = {
            ...this.props.billToData,
        };
        const newShipTo: any = this.props.defaultUseBilling
            ? { ...this.props.billToData }
            : { ...this.props.shipToData };

        if (btAddress && stAddress) {
            newBillTo.address1 = btAddress.line1!;
            if (btAddress.line2) {
                newBillTo.address2 = btAddress.line2;
            }
            if (btAddress.line3) {
                newBillTo.address3 = btAddress.line3;
            }
            newBillTo.country = btAddress.countryObj!;
            newBillTo.state = btAddress.regionObj!;
            newBillTo.city = btAddress.city!;
            newBillTo.postalCode = btAddress.postalCode!;

            newShipTo.address1 = stAddress.line1!;
            if (stAddress.line2) {
                newShipTo.address2 = stAddress.line2;
            }
            if (stAddress.line3) {
                newShipTo.address3 = stAddress.line3;
            }
            newShipTo.country = stAddress.countryObj!;
            newShipTo.state = stAddress.regionObj!;
            newShipTo.city = stAddress.city!;
            newShipTo.postalCode = stAddress.postalCode!;
        }

        this.props.updateBillTo({
            billTo: newBillTo,
            isEdit: true,
            onSuccess: _ => {
                this.props.updateShipTo({
                    billToId: newBillTo.id!,
                    shipTo: newShipTo,
                    isEdit: true,
                    onSuccess: hasCartlines => {
                        if (!hasCartlines) {
                            this.props.history.push(this.props.cartPageLink!);
                        } else {
                            this.redirectToReviewAndSubmit();
                        }
                    },
                });
            },
        });
    };

    render() {
        return (
            <Page data-test-selector="checkoutShipping">
                <CheckoutShippingFormContext.Provider value={this.state}>
                    <form id="checkoutShippingForm" onSubmit={this.handleFormSubmit} noValidate>
                        <Zone zoneName={"Content" as never} contentId={this.props.id as never} />
                    </form>

                    <AvalaraValidationComponentModal
                        key="avalara-create-account"
                        showModal={this.state.showValidationModal}
                        handleModalClose={this.handleCloseValidationModal}
                        handleModalSubmit={this.handleSubmitValidationModal}
                        enteredBtAddress={this.state.enteredBtAddress}
                        enteredStAddress={this.state.enteredStAddress}
                        addresses={this.state.validationAddresses}
                        useBillingAddress={this.props.defaultUseBilling}
                    />
                </CheckoutShippingFormContext.Provider>
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutShippingPage)) as any,
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "CheckoutShippingPage" instead of this constant.
 */
export const CheckoutShippingPageContext = "CheckoutShippingPage";
